import "../styles/templates.scss";
import { useEffect, useMemo } from "react";
import PageLayout from "../components/page-layout";
import Card from "../components/card";
import { GuiButton } from "../components/global-ui/gui-button";
import { GuiSelect } from "../components/global-ui/gui-select";
import GuiCustomDatepickerDropdown from "../components/global-ui/gui-custom-datepicker-dropdown";
import { SearchBox } from "../components/search-box";
import Popover from "../components/popover";
import {
  CheckBoxHeaderButton,
  GuiCheckbox,
} from "../components/global-ui/gui-checkbox";
import Grid, { ColumnHeader, GridCell } from "../components/grid";
import { Icon } from "../components/icon";
import MessageTabs from "./Messages/MessageTabs";
import { ConfirmDeleteModal } from "../components/confirm-delete-modal";
import { TemplatePreviewModal } from "../components/template-preview-modal";
import "react-datepicker/dist/react-datepicker.css";
import { useSelector } from "react-redux";
import { formatGridDate, useUpdateState } from "../utilities/utilities";
import {
  AlertType,
  commMethodLabel,
  commMethodMeta,
  getCommMethodFromConfig,
  Path,
  tabToCommunicationType,
  TEMPLATE_TABS,
  TEMPLATE_FILTER,
  TEMPLATE_SORT_COLUMN,
} from "../utilities/constants";
import navigate from "../utilities/navigation";
import { showToast } from "../components/toast";
import api from "../api/api";
import actions from "../actions/actions";

// ── Module-level helpers ──────────────────────────────────────────────────────

function renderCommMethodBadge(type) {
  const meta = commMethodMeta(type);
  if (!meta) return <span className="templates-empty-value">\u2014</span>;
  return (
    <span className="comm-method-badge">
      <i className={meta.iconClass} aria-hidden="true" />
      <span>{meta.label}</span>
    </span>
  );
}

/**
 * Reusable sortable column header. Renders the label alongside a sort-direction
 * icon and delegates click handling to the parent's onSort callback.
 */
function SortableHeader({ label, column, sortState, onSort, className }) {
  const direction = sortState[column];
  const isActive = direction !== undefined;
  const iconClass =
    direction === true
      ? "fa-arrow-up"
      : direction === false
        ? "fa-arrow-down"
        : "fa-arrow-down-arrow-up";

  return (
    <ColumnHeader className={className} sort={sortState} onSort={onSort}>
      <span className="templates-header-label-with-icon">
        <span>{label}</span>
        <i
          className={`fa ${iconClass} templates-sort-icon${isActive ? " active" : ""}`}
          aria-hidden="true"
        />
      </span>
    </ColumnHeader>
  );
}

// ── Page component ────────────────────────────────────────────────────────────

export default function Templates() {
  const token = useSelector((store) => store.user.token);
  const isMobile = useSelector((store) => store.page.isMobile);

  let [checkboxState, updateCheckboxState, setCheckboxState] = useUpdateState(
    {},
  );
  let [state, updateState] = useUpdateState({
    search: "",
    selectedTab: "all",
    sort: { [TEMPLATE_SORT_COLUMN.LastSaved]: false },
    gridReset: 0,
    filters: [],
    selectedValues: [],
    pendingCategory: null,
    pendingCreatedBy: null,
    showDateFilter: false,
    pendingFromDate: null,
    pendingToDate: null,
    dateFilterError: null, // null | error message
    currentPage: [],
    categoryOptions: [],
    createdByOptions: [],
    showActionsMenu: null,
    loading: false,
    deleteConfirm: null, // null | { ids: number[] }
    deleting: false,
    previewOpen: false,
    previewLoading: false,
    previewTemplate: null,
    previewError: "",
    duplicatingTemplateId: null,
  });
  // Redirect mobile users away — this page is desktop-only.
  useEffect(() => {
    if (isMobile) navigate(Path.Messages);
  }, [isMobile]);
  // Load filter dropdown options once on mount.
  useEffect(() => {
    loadFilterOptions();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Derived values ────────────────────────────────────────────────────────

  /** IDs of all currently-checked rows. */
  const selectedRows = useMemo(
    () => Object.keys(checkboxState).filter((k) => checkboxState[k]),
    [checkboxState],
  );

  /** True when any date filter (from/to) is active in the applied filters. */
  const hasDateFilter = useMemo(
    () =>
      state.filters.some(
        (f) =>
          f.name === TEMPLATE_FILTER.FromDate ||
          f.name === TEMPLATE_FILTER.ToDate,
      ),
    [state.filters],
  );

  /** Formats the applied date range for display in the filter button. */
  const dateFilterText = useMemo(() => {
    const fromDateFilter = state.filters.find(
      (f) => f.name === TEMPLATE_FILTER.FromDate,
    );
    const toDateFilter = state.filters.find(
      (f) => f.name === TEMPLATE_FILTER.ToDate,
    );

    if (fromDateFilter && toDateFilter) {
      const from = new Date(fromDateFilter.value).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "2-digit",
      });
      const to = new Date(toDateFilter.value).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "2-digit",
      });
      return `${from} - ${to}`;
    } else if (fromDateFilter) {
      const from = new Date(fromDateFilter.value).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "2-digit",
      });
      return `From ${from}`;
    } else if (toDateFilter) {
      const to = new Date(toDateFilter.value).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "2-digit",
      });
      return `To ${to}`;
    }
    return "";
  }, [state.filters]);

  const hasPendingDesktopFilters =
    !!state.pendingCategory?.value ||
    !!state.pendingCreatedBy?.value ||
    !!state.pendingFromDate ||
    !!state.pendingToDate;

  const hasAppliedDesktopFilters = state.filters.length > 0;
  const disabled = selectedRows.length === 0;
  const deleteCount = state.deleteConfirm?.ids?.length || 0;
  const isBulkDelete = deleteCount > 1;
  const appliedFromDate = useMemo(
    () => state.filters.find((f) => f.name === TEMPLATE_FILTER.FromDate)?.value,
    [state.filters],
  );
  const appliedToDate = useMemo(
    () => state.filters.find((f) => f.name === TEMPLATE_FILTER.ToDate)?.value,
    [state.filters],
  );

  // ── Data loading ──────────────────────────────────────────────────────────

  async function loadFilterOptions() {
    const result = await api.alerts.templateFilterOptions(token);
    if (result?.success) {
      updateState({
        categoryOptions: result.categories || [],
        createdByOptions: result.createdBy || [],
      });
    }
  }

  async function loadTemplates(page, pageSize, skipTotal) {
    // Helper to extract a single filter value by name.
    const getFilter = (name) =>
      state.filters.find((f) => f.name === name)?.value ?? null;

    updateState({ loading: true });
    const result = await api.alerts.templatesList(
      token,
      state.search,
      tabToCommunicationType(state.selectedTab),
      page,
      pageSize,
      {
        categoryId: getFilter(TEMPLATE_FILTER.Category),
        createdByUserId: getFilter(TEMPLATE_FILTER.CreatedBy),
        fromDate: getFilter(TEMPLATE_FILTER.FromDate),
        toDate: getFilter(TEMPLATE_FILTER.ToDate),
      },
      skipTotal,
    );
    updateState({ loading: false });

    if (result?.success) {
      return {
        list: sortTemplates(result.templates || []),
        totalCount: result.totalCount || 0,
      };
    }
    return { list: [], totalCount: 0 };
  }

  // ── Sorting ───────────────────────────────────────────────────────────────

  function onSort(column) {
    const current = state.sort[column];
    // Cycle: undefined → true (asc) → false (desc) → undefined (off)
    const next =
      current === undefined ? true : current === true ? false : undefined;
    updateState({
      sort: next === undefined ? {} : { [column]: next },
      gridReset: state.gridReset + 1,
    });
  }

  function sortTemplates(list) {
    const activeColumn = Object.keys(state.sort).find(
      (col) => state.sort[col] !== undefined,
    );
    if (!activeColumn) return list;

    const sorted = [...list].sort((a, b) =>
      compareTemplateValues(activeColumn, a, b),
    );
    return state.sort[activeColumn] ? sorted : sorted.reverse();
  }

  function compareTemplateValues(column, a, b) {
    // Shared case-insensitive locale compare for string columns.
    const localeCompare = (x, y) =>
      (x || "").localeCompare(y || "", undefined, { sensitivity: "base" });

    switch (column) {
      case TEMPLATE_SORT_COLUMN.LastSaved:
        return (
          (a.lastSaved ? new Date(a.lastSaved).getTime() : 0) -
          (b.lastSaved ? new Date(b.lastSaved).getTime() : 0)
        );
      case TEMPLATE_SORT_COLUMN.Name:
        return localeCompare(a.name, b.name);
      case TEMPLATE_SORT_COLUMN.CommMethod:
        return localeCompare(
          commMethodLabel(a.communicationMethod),
          commMethodLabel(b.communicationMethod),
        );
      case TEMPLATE_SORT_COLUMN.Category:
        return localeCompare(a.category, b.category);
      case TEMPLATE_SORT_COLUMN.CreatedBy:
        return localeCompare(a.createdBy, b.createdBy);
      default:
        return 0;
    }
  }

  // ── Search & filters ──────────────────────────────────────────────────────

  function setSearch(search) {
    const normalized = search.trim().length >= 3 ? search.trim() : "";
    if (normalized === state.search) return;
    updateState({ search: normalized, gridReset: state.gridReset + 1 });
  }

  function onDesktopFilterChange(name, value) {
    if (name === TEMPLATE_FILTER.Category)
      updateState({ pendingCategory: value });
    else if (name === TEMPLATE_FILTER.CreatedBy)
      updateState({ pendingCreatedBy: value });
  }

  function applyDesktopFilters() {
    const values = [];

    if (state.pendingCategory) {
      values.push({ name: TEMPLATE_FILTER.Category, ...state.pendingCategory });
    }
    if (state.pendingCreatedBy) {
      values.push({
        name: TEMPLATE_FILTER.CreatedBy,
        ...state.pendingCreatedBy,
      });
    }
    if (state.pendingFromDate) {
      values.push({
        name: TEMPLATE_FILTER.FromDate,
        value: state.pendingFromDate,
        label: "From: " + new Date(state.pendingFromDate).toLocaleDateString(),
      });
    }
    if (state.pendingToDate) {
      values.push({
        name: TEMPLATE_FILTER.ToDate,
        value: state.pendingToDate,
        label: "To: " + new Date(state.pendingToDate).toLocaleDateString(),
      });
    }

    updateState({
      filters: values,
      // selectedValues excludes date filters (used by filter-tag display).
      selectedValues: values.filter(
        (f) =>
          f.name !== TEMPLATE_FILTER.FromDate &&
          f.name !== TEMPLATE_FILTER.ToDate,
      ),
      showDateFilter: false,
      gridReset: state.gridReset + 1,
    });
  }

  function resetDesktopFilters() {
    updateState({
      filters: [],
      selectedValues: [],
      pendingCategory: null,
      pendingCreatedBy: null,
      pendingFromDate: null,
      pendingToDate: null,
      showDateFilter: false,
      gridReset: state.gridReset + 1,
    });
  }

  function toPickerDate(value) {
    if (!value) return null;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  function clearDateFilter() {
    updateState({
      pendingFromDate: null,
      pendingToDate: null,
      dateFilterError: null,
      showDateFilter: false,
    });
  }

  /**
   * Validates the date range: To Date must be >= From Date.
   * Returns error message if invalid, null if valid.
   */
  function validateDateRange(fromDate, toDate) {
    if (!fromDate || !toDate) return null; // No error if either is empty

    const from = new Date(fromDate).getTime();
    const to = new Date(toDate).getTime();

    if (to < from) {
      return "To Date must be the same as or later than From Date";
    }
    return null;
  }

  /**
   * Closes the date range popover after validating and applying the date filter.
   * Clears error if dates are valid.
   */
  function closeDateFilterPopover() {
    const error = validateDateRange(state.pendingFromDate, state.pendingToDate);
    if (error) {
      updateState({ dateFilterError: error });
      return;
    }

    // Build the new filters list with the date filters
    const dateFilters = [];
    if (state.pendingFromDate) {
      dateFilters.push({
        name: TEMPLATE_FILTER.FromDate,
        value: state.pendingFromDate,
        label: "From: " + new Date(state.pendingFromDate).toLocaleDateString(),
      });
    }
    if (state.pendingToDate) {
      dateFilters.push({
        name: TEMPLATE_FILTER.ToDate,
        value: state.pendingToDate,
        label: "To: " + new Date(state.pendingToDate).toLocaleDateString(),
      });
    }

    // Keep non-date filters and add date filters
    const nonDateFilters = state.filters.filter(
      (f) =>
        f.name !== TEMPLATE_FILTER.FromDate &&
        f.name !== TEMPLATE_FILTER.ToDate,
    );
    const newFilters = [...nonDateFilters, ...dateFilters];

    updateState({
      filters: newFilters,
      showDateFilter: false,
      dateFilterError: null,
      gridReset: state.gridReset + 1,
    });
  }

  // ── Selection & bulk actions ──────────────────────────────────────────────

  function toggleAllCheckboxes() {
    const anySelected = Object.keys(checkboxState).some(
      (k) => checkboxState[k],
    );
    if (anySelected || !state.currentPage?.length) {
      setCheckboxState({});
    } else {
      // Select every row on the current page.
      setCheckboxState(
        Object.fromEntries(state.currentPage.map((t) => [t.id, true])),
      );
    }
  }

  function onPageChanged(list) {
    setCheckboxState({});
    updateState({ currentPage: list });
  }

  function confirmDelete(ids) {
    updateState({ showActionsMenu: null, deleteConfirm: { ids } });
  }

  async function executeDelete() {
    const ids = state.deleteConfirm?.ids;
    if (!ids?.length) return;

    updateState({ deleting: true });
    const result = await actions.alerts.deleteTemplates(ids);
    updateState({ deleting: false, deleteConfirm: null });

    if (result?.success) {
      setCheckboxState({});
      updateState({ gridReset: state.gridReset + 1 });
      showToast(
        "success",
        ids.length === 1
          ? "Template deleted successfully."
          : `${ids.length} templates deleted successfully.`,
      );
    } else {
      showToast("error", "Failed to delete template(s). Please try again.");
    }
  }

  async function openPreview(templateId) {
    updateState({
      showActionsMenu: null,
      previewOpen: true,
      previewLoading: true,
      previewTemplate: null,
      previewError: "",
    });

    const result = await api.alerts.getTemplateById(token, templateId);
    if (result?.success) {
      let previewResult = result;
      const alertTypeId = result.alertTypeId ?? result.AlertTypeId;
      const templateType = result.templateType ?? result.TemplateType;
      const previewMethod = getCommMethodFromConfig(alertTypeId, templateType);
      const templateContent = result.template ?? result.Template ?? "";

      if (previewMethod === AlertType.VoiceIVR && templateContent) {
        const ttsPreview = await api.file.textToSpeech(token, templateContent);
        if (ttsPreview?.success && (ttsPreview.fileUrl || ttsPreview.FileUrl)) {
          previewResult = {
            ...result,
            template: ttsPreview.fileUrl || ttsPreview.FileUrl,
          };
        }
      }

      updateState({
        previewLoading: false,
        previewTemplate: previewResult,
      });
    } else {
      updateState({
        previewLoading: false,
        previewError:
          result?.errorMessage || "Failed to load template preview.",
      });
    }
  }

  async function executeDuplicate(templateId) {
    updateState({ showActionsMenu: null, duplicatingTemplateId: templateId });
    const result = await actions.alerts.duplicateTemplate(templateId);
    updateState({ duplicatingTemplateId: null });

    if (result?.success) {
      updateState({ gridReset: state.gridReset + 1 });
      showToast("success", "Template duplicated successfully.");
    } else {
      showToast("error", "Failed to duplicate template. Please try again.");
    }
  }

  function handleTabChange(tabId) {
    setCheckboxState({});
    updateState({ selectedTab: tabId, gridReset: state.gridReset + 1 });
  }

  return (
    <PageLayout
      className="templates-page"
      title="Templates"
      buttonBar={
        <GuiButton
          className="btn-primary templates-header-button"
          onClick={() => navigate(Path.CreateTemplate)}
          dataQa="new-template-btn"
        >
          <Icon icon="plus" weight="400" /> New Template
        </GuiButton>
      }
    >
      <Card className="templates templates-card">
        {/* ── Search / filter bar ── */}
        <div className="page-layout-content-header templates-filters-row">
          <SearchBox onSearchChange={setSearch} minLength={3} />

          <GuiSelect
            className="templates-inline-select"
            label={TEMPLATE_FILTER.Category}
            name={TEMPLATE_FILTER.Category}
            options={state.categoryOptions}
            value={state.pendingCategory?.value}
            onChange={onDesktopFilterChange}
            isSearchable={false}
            placeholder="All"
          />

          <GuiSelect
            className="templates-inline-select"
            label={TEMPLATE_FILTER.CreatedBy}
            name={TEMPLATE_FILTER.CreatedBy}
            options={state.createdByOptions}
            value={state.pendingCreatedBy?.value}
            onChange={onDesktopFilterChange}
            isSearchable={false}
            placeholder="All"
          />

          {/* Date range popover — pending dates are committed with the main Apply button. */}
          <div className="popover-container">
            <GuiButton
              className={[
                "btn-outline filter-button templates-date-filter-trigger",
                state.showDateFilter ? "active" : "",
                hasDateFilter ? "has-filters" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              onClick={() => {
                if (!state.showDateFilter) {
                  // When opening, populate pending dates from applied filters
                  const fromDateFilter = state.filters.find(
                    (f) => f.name === TEMPLATE_FILTER.FromDate,
                  );
                  const toDateFilter = state.filters.find(
                    (f) => f.name === TEMPLATE_FILTER.ToDate,
                  );
                  updateState({
                    showDateFilter: true,
                    pendingFromDate:
                      fromDateFilter?.value || state.pendingFromDate,
                    pendingToDate: toDateFilter?.value || state.pendingToDate,
                  });
                } else {
                  updateState({ showDateFilter: false });
                }
              }}
            >
              <span className="templates-date-filter-label">
                Filter by Date
              </span>
              <span className="templates-date-filter-text">
                <i className="fa fa-calendar" aria-hidden="true" />
                {dateFilterText && <span>{dateFilterText}</span>}
              </span>
              <Icon icon="chevron-down" weight="400" />
            </GuiButton>
            <Popover
              show={state.showDateFilter}
              className="no-arrow filter-popover template-date-filter-popover"
              onHide={() => updateState({ showDateFilter: false })}
            >
              <div className="template-date-filter-body">
                <div className="date-range-inputs">
                  <GuiCustomDatepickerDropdown
                    dateLabel="From Date"
                    onDateChange={(date) =>
                      updateState({
                        pendingFromDate: date,
                        dateFilterError: null,
                      })
                    }
                    dateSelected={state.pendingFromDate}
                  />
                  <GuiCustomDatepickerDropdown
                    dateLabel="To Date"
                    onDateChange={(date) =>
                      updateState({
                        pendingToDate: date,
                        dateFilterError: null,
                      })
                    }
                    dateSelected={state.pendingToDate}
                  />
                </div>
                {state.dateFilterError && (
                  <div className="date-filter-error">
                    <i
                      className="fa fa-exclamation-circle"
                      aria-hidden="true"
                    />
                    <span>{state.dateFilterError}</span>
                  </div>
                )}
                <div className="buttons">
                  <GuiButton className="btn-text" onClick={clearDateFilter}>
                    Clear
                  </GuiButton>
                  <GuiButton onClick={closeDateFilterPopover}>Apply</GuiButton>
                </div>
              </div>
            </Popover>
          </div>

          <GuiButton
            className={[
              "btn-outline templates-apply-button",
              hasAppliedDesktopFilters ? "has-applied" : "",
              hasPendingDesktopFilters ? "has-pending" : "",
            ]
              .filter(Boolean)
              .join(" ")}
            onClick={applyDesktopFilters}
            disabled={!hasPendingDesktopFilters}
          >
            Apply
          </GuiButton>

          <GuiButton
            className="btn-text templates-reset-button"
            onClick={resetDesktopFilters}
            disabled={!hasPendingDesktopFilters && !hasAppliedDesktopFilters}
          >
            Reset
          </GuiButton>
        </div>

        {/* ── Communication type tabs ── */}
        <div className="templates-tabs-row">
          <MessageTabs
            tabs={TEMPLATE_TABS}
            selectedTab={state.selectedTab}
            onTabChange={handleTabChange}
          />
          {!disabled && (
            <div className="templates-bulk-actions">
              <span className="selected-count">
                {selectedRows.length} selected
              </span>
              <GuiButton
                className="btn-outline btn-danger-outline"
                onClick={() => confirmDelete(selectedRows.map(Number))}
              >
                <i className="fa fa-trash-alt" /> Delete
              </GuiButton>
            </div>
          )}
        </div>
        {/* {!disabled && (
          <div className="templates-selection-actions">
            <GuiButton
              className="btn-outline templates-delete-all-button"
              onClick={() => confirmDelete(selectedRows.map(Number))}
            >
              <i className="fas fa-trash-alt" aria-hidden="true" />
              <span>Delete All</span>
            </GuiButton>
          </div>
        )} */}

        {/* ── Grid ── */}
        <Grid
          header={
            <div className="header-row row">
              <ColumnHeader className="check">
                <CheckBoxHeaderButton
                  checked={
                    !!Object.keys(checkboxState).find((k) => checkboxState[k])
                  }
                  onClick={toggleAllCheckboxes}
                />
              </ColumnHeader>
              <SortableHeader
                label="Template Name"
                column={TEMPLATE_SORT_COLUMN.Name}
                className="name"
                sortState={state.sort}
                onSort={onSort}
              />
              <SortableHeader
                label="Communication Method"
                column={TEMPLATE_SORT_COLUMN.CommMethod}
                className="comm-method"
                sortState={state.sort}
                onSort={onSort}
              />
              <SortableHeader
                label="Template Category"
                column={TEMPLATE_SORT_COLUMN.Category}
                className="category"
                sortState={state.sort}
                onSort={onSort}
              />
              <SortableHeader
                label="Created By"
                column={TEMPLATE_SORT_COLUMN.CreatedBy}
                className="created-by"
                sortState={state.sort}
                onSort={onSort}
              />
              <SortableHeader
                label="Last Saved"
                column={TEMPLATE_SORT_COLUMN.LastSaved}
                className="last-saved"
                sortState={state.sort}
                onSort={onSort}
              />
              <ColumnHeader className="actions" />
            </div>
          }
          itemTemplate={(t) => (
            <div
              className={"row" + (checkboxState[t.id] ? " selected-row" : "")}
              key={t.id}
            >
              <GridCell className="check">
                <GuiCheckbox
                  name={"" + t.id}
                  value={checkboxState[t.id] || false}
                  onChange={updateCheckboxState}
                  onClick={(e) => e.stopPropagation()}
                />
              </GridCell>
              <GridCell className="name">{t.name}</GridCell>
              <GridCell className="comm-method">
                {renderCommMethodBadge(t.communicationMethod)}
              </GridCell>
              <GridCell className="category">{t.category || "\u2014"}</GridCell>
              <GridCell className="created-by">
                {t.createdBy || "\u2014"}
              </GridCell>
              <GridCell className="last-saved">
                {t.lastSaved ? formatGridDate(t.lastSaved) : "\u2014"}
              </GridCell>
              <GridCell className="actions">
                <div className="templates-action-buttons">
                  <i
                    className="far fa-pen action-icon"
                    title="Edit"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(Path.EditTemplate.replace(":id", t.id));
                    }}
                  />
                  <button
                    type="button"
                    className="action-icon action-icon-button"
                    title="More options"
                    aria-label={`Template actions for ${t.name}`}
                    aria-haspopup="menu"
                    aria-expanded={state.showActionsMenu === t.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      updateState({
                        showActionsMenu:
                          state.showActionsMenu === t.id ? null : t.id,
                      });
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Escape") {
                        e.preventDefault();
                        updateState({ showActionsMenu: null });
                      }
                    }}
                  >
                    <i className="far fa-ellipsis-h" />
                  </button>
                </div>
                <Popover
                  show={state.showActionsMenu === t.id}
                  className="no-arrow anchor-right popover-menu actions-menu"
                  onHide={() => updateState({ showActionsMenu: null })}
                >
                  <div
                    role="menu"
                    className="menu-items"
                    onKeyDown={(e) => {
                      if (e.key === "Escape") {
                        e.preventDefault();
                        updateState({ showActionsMenu: null });
                      } else if (e.key === "ArrowDown" || e.key === "ArrowUp") {
                        e.preventDefault();
                        const buttons = Array.from(
                          e.currentTarget.querySelectorAll("button.menu-item"),
                        );
                        const currentIndex = buttons.indexOf(
                          document.activeElement,
                        );
                        const direction = e.key === "ArrowDown" ? 1 : -1;
                        const nextIndex =
                          currentIndex < 0
                            ? 0
                            : (currentIndex + direction + buttons.length) %
                              buttons.length;
                        buttons[nextIndex]?.focus();
                      }
                    }}
                  >
                    <button
                      type="button"
                      role="menuitem"
                      className="menu-item"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(Path.EditTemplate.replace(":id", t.id));
                      }}
                    >
                      <i className="far fa-edit" aria-hidden="true" />
                      <span>Edit</span>
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      className="menu-item"
                      onClick={(e) => {
                        e.stopPropagation();
                        openPreview(t.id);
                      }}
                    >
                      <i className="far fa-eye" aria-hidden="true" />
                      <span>Preview</span>
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      className="menu-item"
                      disabled={state.duplicatingTemplateId === t.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        executeDuplicate(t.id);
                      }}
                    >
                      <i className="far fa-copy" aria-hidden="true" />
                      <span>Duplicate</span>
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      className="menu-item menu-item-danger"
                      onClick={(e) => {
                        e.stopPropagation();
                        confirmDelete([t.id]);
                      }}
                    >
                      <i className="far fa-trash" aria-hidden="true" />
                      <span>Delete</span>
                    </button>
                  </div>
                </Popover>
              </GridCell>
            </div>
          )}
          mobileItemTemplate={(t) => (
            <div className="row" key={t.id}>
              <div className="check">
                <div className="check-row">
                  <div className="check-title">
                    <GuiCheckbox
                      name={"" + t.id}
                      value={checkboxState[t.id] || false}
                      onChange={updateCheckboxState}
                    />
                  </div>
                  <div className="check-body">
                    <div className="check-body-name">
                      <div className="check-body-name-header">{t.name}</div>
                    </div>
                    <div className="check-body-details-mobile">
                      <div className="check-body-details-mobile-header">
                        <span>
                          {commMethodLabel(t.communicationMethod)} &middot;{" "}
                          {t.category || "\u2014"}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="tridot">
                    <i
                      className="fa fa-ellipsis-vertical mobile-action-icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        updateState({ showActionsMenu: t.id });
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
          mobileItemHeight={65}
          onItemsNeeded={loadTemplates}
          onPageChanged={onPageChanged}
          search={state.search}
          gridReset={state.gridReset}
        />

        {state.deleteConfirm && (
          <ConfirmDeleteModal
            status="remove"
            className="template-delete-confirm-modal"
            showClose={true}
            title={isBulkDelete ? "Delete Templates?" : "Delete Template?"}
            message="This action cannot be undone."
            confirmText={isBulkDelete ? "Delete Templates" : "Delete Template"}
            deleting={state.deleting}
            onDelete={executeDelete}
            onCancel={() => updateState({ deleteConfirm: null })}
          />
        )}
        {state.previewOpen && (
          <TemplatePreviewModal
            template={state.previewTemplate}
            loading={state.previewLoading}
            error={state.previewError}
            onClose={() =>
              updateState({
                previewOpen: false,
                previewError: "",
                previewTemplate: null,
              })
            }
          />
        )}
      </Card>
    </PageLayout>
  );
}
